import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "reactstrap";
import SimpleBar from "simplebar-react";
import { obtenerSesionContexto } from "../../helpers/sesionesScope";

type ModuleNode = {
  id: string;
  label: string;
  link?: string;
  permission?: string;
  icon?: string;
  children?: ModuleNode[];
};

type SearchEntry = {
  id: string;
  label: string;
  link: string;
  trail: string;
  icon?: string;
  searchText: string;
};

const MAX_RESULTS = 12;

const MODULE_TREE: ModuleNode[] = [
  {
    id: "modules",
    label: "Módulos",
    icon: "ri-stack-line",
    children: [
      {
        id: "modules-tesoreria",
        label: "Tesorería",
        children: [
          {
            id: "rendicion-caja",
            label: "Rendición de caja",
            link: "/rendicion-caja",
            permission: "modulos.rendicion_caja",
          },
          {
            id: "conciliacion-caja",
            label: "Conciliación de caja",
            link: "/conciliacion-caja",
            permission: "modulos.conciliacion_caja",
          },
        ],
      },
      {
        id: "modules-contabilidad",
        label: "Contabilidad",
        children: [
          {
            id: "contabilidad-asientos",
            label: "Asientos contables",
            link: "/contabilidad/asientos",
            permission: "contabilidad.asientos",
          },
          {
            id: "contabilidad-plan-cuentas",
            label: "Plan de cuentas",
            link: "/contabilidad/plan-cuentas",
            permission: "contabilidad.plan_cuentas",
          },
          {
            id: "contabilidad-articulos-cuentas",
            label: "Artículos y cuentas",
            link: "/contabilidad/articulos-cuentas",
            permission: "contabilidad.articulos_cuentas",
          },
          {
            id: "contabilidad-reportes",
            label: "Reportes contables",
            link: "/contabilidad/reportes",
            permission: "contabilidad.reportes",
          },
        ],
      },
      {
        id: "modules-produccion",
        label: "Fábrica",
        children: [
          {
            id: "produccion-recetas",
            label: "Recetas de producción",
            link: "/produccion/recetas",
            permission: "produccion.recetas",
          },
          {
            id: "produccion-ordenes",
            label: "Órdenes de producción",
            link: "/produccion/ordenes",
            permission: "produccion.ordenes",
          },
        ],
      },
      {
        id: "modules-rrhh",
        label: "Recursos Humanos",
        children: [
          {
            id: "rrhh-importar-planilla",
            label: "Planilla XLSM",
            link: "/rrhh/importar-planilla",
            permission: "rrhh.importar_planilla",
          },
          {
            id: "rrhh-liquidacion",
            label: "Liquidación salario",
            link: "/rrhh/liquidacion-salario",
            permission: "rrhh.liquidacion_salario",
          },
          {
            id: "rrhh-reportes",
            label: "Reportes RRHH",
            link: "/rrhh/reportes",
            permission: "rrhh.reportes",
          },
          {
            id: "rrhh-marcaciones",
            label: "Marcaciones",
            link: "/rrhh/marcaciones",
            permission: "rrhh.marcaciones",
          },
          {
            id: "rrhh-consumision",
            label: "Consumisión",
            link: "/rrhh/consumision",
            permission: "rrhh.consumision",
          },
        ],
      },
    ],
  },
  {
    id: "informes",
    label: "Informes",
    icon: "ri-line-chart-line",
    children: [
      {
        id: "informes-compras",
        label: "Compras",
        link: "/informes/compras",
        permission: "informes.stock_compras",
      },
      {
        id: "informes-inventarios",
        label: "Inventarios",
        link: "/informes/inventarios",
        permission: "informes.stock_inventarios",
      },
      {
        id: "informes-transferencias",
        label: "Transferencias",
        link: "/informes/transferencias",
        permission: "informes.stock_transferencias",
      },
      {
        id: "informes-control-stock",
        label: "Control de Stock",
        link: "/informes/online/control-stock",
        permission: "informes.stock_inventarios",
      },
      {
        id: "informes-finanzas",
        label: "Finanzas",
        link: "/informes/finanzas",
        permission: "informes.finanzas",
      },
      {
        id: "informes-finanzas-cartera",
        label: "Finanzas - Cartera vencida",
        link: "/informes/online/finanzas/cartera-vencida",
        permission: "informes.finanzas",
      },
      {
        id: "informes-rrhh",
        label: "Recursos Humanos",
        link: "/informes/rrhh",
        permission: "informes.rrhh",
      },
      {
        id: "informes-tesoreria",
        label: "Tesorería",
        link: "/informes/tesoreria",
        permission: "informes.tesoreria",
      },
      {
        id: "informes-contabilidad",
        label: "Contabilidad",
        link: "/informes/contabilidad",
        permission: "informes.contabilidad",
      },
      {
        id: "informes-logistica",
        label: "Logística",
        link: "/informes/logistica",
        permission: "informes.logistica",
      },
      {
        id: "informes-produccion",
        label: "Producción",
        link: "/informes/produccion",
        permission: "informes.produccion",
      },
      {
        id: "informes-gerencia",
        label: "Gerencia",
        link: "/informes/gerencia",
        permission: "informes.gerencia",
      },
      {
        id: "informes-informatica",
        label: "Informática",
        link: "/informes/informatica",
        permission: "informes.informatica",
      },
    ],
  },
  {
    id: "ajustes",
    label: "Ajustes",
    icon: "ri-settings-3-line",
    link: "/ajustes",
    permission: "ajustes.acceder",
  },
];

const normalizeText = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const isNavigable = (item: ModuleNode) => {
  const link = typeof item.link === "string" ? item.link : "";
  const invalidLink = link === "/#" || link === "#" || link.trim() === "";
  return !invalidLink;
};

const collectChildren = (item: ModuleNode) => {
  if (Array.isArray(item.children)) {
    return item.children;
  }
  return [];
};

const flattenMenuItems = (
  items: ModuleNode[],
  parents: string[] = [],
  inheritedIcon?: string
): SearchEntry[] => {
  let results: SearchEntry[] = [];
  items.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }
    const label: string = item.label ?? "";
    const nextParents = label ? [...parents, label] : parents;
    const iconToUse = item.icon || inheritedIcon;
    if (isNavigable(item)) {
      const trail = parents.filter(Boolean).join(" / ");
      const id = item.id || `${item.link}-${label}-${trail}`;
      results.push({
        id,
        label,
        link: item.link || "#",
        trail,
        icon: iconToUse,
        searchText: normalizeText(nextParents.join(" ")),
      });
    }
    const children = collectChildren(item);
    if (children.length > 0) {
      results = results.concat(flattenMenuItems(children, nextParents, iconToUse));
    }
  });
  return results;
};

const SearchOption = () => {
  const [query, setQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLFormElement | null>(null);

  const contextoSesion = useMemo(() => obtenerSesionContexto(), []);
  const permisosUsuario = useMemo(
    () => contextoSesion?.permisos ?? [],
    [contextoSesion]
  );
  const tienePermiso = useCallback(
    (codigo?: string) => {
      if (!codigo) return true;
      return permisosUsuario.includes(codigo) || permisosUsuario.includes("general.solo_admin");
    },
    [permisosUsuario]
  );

  const searchIndex = useMemo(() => {
    const filtrarPorPermiso = (nodes: ModuleNode[]): ModuleNode[] => {
      return nodes
        .map((node) => {
          if (!tienePermiso(node.permission)) {
            return null;
          }
          const hijos = collectChildren(node);
          if (hijos.length > 0) {
            const hijosFiltrados = filtrarPorPermiso(hijos);
            return { ...node, children: hijosFiltrados };
          }
          return node;
        })
        .filter((node): node is ModuleNode => Boolean(node));
    };

    const permitidos = filtrarPorPermiso(MODULE_TREE);
    return flattenMenuItems(permitidos, []);
  }, [tienePermiso]);

  const normalizedQuery = useMemo(() => normalizeText(query.trim()), [query]);

  const filteredResults = useMemo(() => {
    if (!normalizedQuery) {
      return [];
    }
    return searchIndex
      .filter((entry) => entry.searchText.includes(normalizedQuery))
      .slice(0, MAX_RESULTS);
  }, [normalizedQuery, searchIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClear = () => {
    setQuery("");
    setIsDropdownOpen(false);
  };

  const handleResultClick = () => {
    handleClear();
  };

  const handleFocus = () => {
    if (query.trim()) {
      setIsDropdownOpen(true);
    }
  };

  const handleChange = (value: string) => {
    setQuery(value);
    setIsDropdownOpen(value.trim().length > 0);
  };

  const showClearButton = query.length > 0;

  return (
    <React.Fragment>
      <form className="app-search d-none d-md-block" ref={containerRef}>
        <div className="position-relative">
          <Input
            type="text"
            className="form-control"
            placeholder="Buscar módulos..."
            id="search-options"
            value={query}
            onFocus={handleFocus}
            onChange={(e) => handleChange(e.target.value)}
          />
          <span className="mdi mdi-magnify search-widget-icon"></span>
          <span
            className={`mdi mdi-close-circle search-widget-icon search-widget-icon-close ${
              showClearButton ? "" : "d-none"
            }`}
            id="search-close-options"
            role="button"
            tabIndex={0}
            onClick={handleClear}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClear();
              }
            }}
          ></span>
        </div>
        <div
          className={`dropdown-menu dropdown-menu-lg ${isDropdownOpen ? "show" : ""}`}
          id="search-dropdown"
        >
          <SimpleBar style={{ maxHeight: "320px" }}>
            {normalizedQuery && filteredResults.length === 0 && (
              <div className="text-center text-muted py-4 px-3">
                No encontramos módulos que coincidan con "{query}".
              </div>
            )}
            {filteredResults.map((entry) => (
              <Link
                to={entry.link}
                key={entry.id}
                className="dropdown-item notify-item py-2"
                onClick={handleResultClick}
              >
                <div className="d-flex align-items-start">
                  <i className={`${entry.icon ?? "ri-folder-line"} text-muted fs-18 me-3`}></i>
                  <div className="flex-grow-1">
                    <h6 className="mb-0">{entry.label}</h6>
                    {entry.trail && <small className="text-muted">{entry.trail}</small>}
                  </div>
                </div>
              </Link>
            ))}
          </SimpleBar>
        </div>
      </form>
    </React.Fragment>
  );
};

export default SearchOption;
